import axios from 'axios';
import { BASE_URL, VIDEO_SOURCE_API_PATH } from 'constants/api';
import { contentService } from 'content/service/contentService';
import { ContentInterface } from 'content/type';
import { ApiService } from 'service/api/apiService';
import { FetchResult } from 'type/apiType';
import {
  FEED_FORMAT_FALLBACK_OPTIONS,
  FEED_FORMAT_LABELS,
  DEFAULT_LANGUAGE_FALLBACK_OPTIONS,
  VIDEO_SOURCE_FIELD_METADATA_FALLBACK,
} from '../constants/feedFormat';
import {
  BatchJobResponse,
  VideoSourceChoiceOption,
  VideoSourceFieldMetadata,
  VideoSourceInterface,
  VideoSourceSelectItemInterface,
  VideoSourceVideoListInterface,
} from '../type';

type DrfChoice = { value: string | number; display_name: string };
type DrfFieldInfo = { choices?: DrfChoice[] };

// labels: 서버 display_name이 한국어인 필드의 화면 표기를 덮기 위한 매핑
const toChoiceOptions = (
  field?: DrfFieldInfo,
  labels?: Record<string, string>
): VideoSourceChoiceOption[] | undefined =>
  field && field.choices && field.choices.length > 0
    ? field.choices.map(choice => {
        const value = String(choice.value);
        return { value, label: labels?.[value] ?? `${choice.display_name} (${value})` };
      })
    : undefined;

class VideoSourceService {
  constructor(private readonly apiService: ApiService<VideoSourceInterface>) {}

  public async fetch(queryParams: URLSearchParams): Promise<FetchResult<VideoSourceInterface>> {
    return await this.apiService.fetch(queryParams);
  }

  public async fetchById(id: number): Promise<VideoSourceInterface> {
    return await this.apiService.fetchById(id);
  }

  public async create(videoSource: VideoSourceInterface): Promise<void> {
    await this.apiService.create(videoSource);
  }

  public async update(id: number, videoSource: VideoSourceInterface): Promise<void> {
    await this.apiService.update(id, videoSource);
  }

  public async bulkOdkSync(startFromBeginning: boolean): Promise<void> {
    return await axios.post(`${BASE_URL}${VIDEO_SOURCE_API_PATH}odk-bulk/`, {
      reset: startFromBeginning,
    });
  }

  public async getODKItem(): Promise<VideoSourceInterface> {
    const response = await axios.get(`${BASE_URL}${VIDEO_SOURCE_API_PATH}odk/`);
    return response.data;
  }

  public async generate(id: number): Promise<BatchJobResponse> {
    return await axios.post(`${BASE_URL}${VIDEO_SOURCE_API_PATH}${id}/batch/`);
  }

  /**
   * DRF OPTIONS로 쓰기 가능한 필드와 choices를 읽어온다.
   * 백엔드 계보에 따라 feed_format choices(auto/fastview vs auto/zype/odk)와
   * default_season/default_language 존재 여부가 다르므로 하드코딩하지 않는다.
   * OPTIONS가 막힌 환경에서는 폴백(전체 노출)으로 동작한다.
   */
  public async fetchFieldMetadata(): Promise<VideoSourceFieldMetadata> {
    try {
      const response = await axios.options(`${BASE_URL}${VIDEO_SOURCE_API_PATH}`);
      const actions = response.data?.actions;
      const fields: Record<string, DrfFieldInfo> = actions?.POST ?? actions?.PUT;
      if (!fields || typeof fields !== 'object') {
        return VIDEO_SOURCE_FIELD_METADATA_FALLBACK;
      }
      return {
        supportedFields: Object.keys(fields),
        feedFormatOptions:
          toChoiceOptions(fields.feed_format, FEED_FORMAT_LABELS) ?? FEED_FORMAT_FALLBACK_OPTIONS,
        defaultLanguageOptions:
          toChoiceOptions(fields.default_language) ?? DEFAULT_LANGUAGE_FALLBACK_OPTIONS,
        fromServer: true,
      };
    } catch {
      return VIDEO_SOURCE_FIELD_METADATA_FALLBACK;
    }
  }

  public async countMrssItems(id: number): Promise<number> {
    const response = await axios.get(`${BASE_URL}${VIDEO_SOURCE_API_PATH}${id}/item-count/`);
    return response.data.item_count;
  }

  public async delete(id: number): Promise<void> {
    await this.apiService.delete(id);
  }

  public async getVideoSourceSelectItemList(): Promise<VideoSourceSelectItemInterface[]> {
    const items = await this.apiService.fetch();
    return items.results.map(item => ({
      id: item.id,
      name: item.name,
    }));
  }

  public async fetchContentList(
    id: number,
    queryParams: URLSearchParams
  ): Promise<VideoSourceVideoListInterface[]> {
    if (id === 0) {
      const { results: videoList } = await contentService.fetch(queryParams);
      // TODO: Currently fetching the video list instead of the Content Item List, so handling it this way.
      // Need to change to fetch Content Item List in the future
      return videoList.map((item: ContentInterface) => ({
        id: item.id,
        guid: 0,
        original_content_url: '',
        status: 'completed',
        video_source: 0,
        content: item,
      })) as VideoSourceVideoListInterface[];
    }

    const response = await axios.get(`${BASE_URL}${VIDEO_SOURCE_API_PATH}${id}/video-list/`, {
      params: queryParams,
    });
    return response.data ? response.data.results : [];
  }
}

export const videoSourceService = new VideoSourceService(new ApiService(VIDEO_SOURCE_API_PATH));
